import { EditOutlined } from '@ant-design/icons'
import { Avatar, Button, Tag } from '@lmc/ui-kit'
import { Text } from '@lmc/ui-kit/src/general'
import { Popover } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import styled from 'styled-components'
import {
  ReconciliationPayBy,
  type ReconciliationRow
} from '../../Reports/types/reconciliation'
import WarningPopover from '../RotaBuilder/common/components/popovers/WarningPopover'
import {
  formatDisplayName,
  formatMinutes,
  formatTime,
  isCompleteActualTime
} from './reconciliationUtils'

export type GetReconciliationTableColumnsParams = {
  t: (key: string, options?: Record<string, unknown>) => string
  nominative: string
  onDontPay: (record: ReconciliationRow) => void
  onEditPayAmount: (record: ReconciliationRow) => void
}

export function getReconciliationTableColumns({
  t,
  nominative,
  onDontPay,
  onEditPayAmount
}: GetReconciliationTableColumnsParams): ColumnsType<ReconciliationRow> {
  const columns: ColumnsType<ReconciliationRow> = [
    {
      title: (
        <LeftmostCellContainer>
          <Text weight="medium">{t('RECONCILIATION_TABLE_STATUS')}</Text>
        </LeftmostCellContainer>
      ),
      key: 'status',
      sorter: true,
      width: 148,
      render: (_, record) => {
        const inner = record.isCanceled ? (
          <ReconciliationTableTag>
            {t('RECONCILIATION_STATUS_CANCELLED')}
          </ReconciliationTableTag>
        ) : record.status === 'reconciled' ? (
          <ReconciliationTableTag color="success">
            {t('RECONCILIATION_STATUS_RECONCILED')}
          </ReconciliationTableTag>
        ) : (
          <ReconciliationTableTag color="orange">
            {t('RECONCILIATION_STATUS_NEEDS_ATTENTION')}
          </ReconciliationTableTag>
        )
        return <LeftmostCellContainer>{inner}</LeftmostCellContainer>
      }
    },
    {
      title: <Text weight="medium">{t('RECONCILIATION_TABLE_DATE')}</Text>,
      dataIndex: 'date',
      key: 'date',
      sorter: true,
      defaultSortOrder: 'descend',
      width: 124,
      render: (date: string) => (
        <CellText>
          <CellPrimaryText>{dayjs(date).format('DD-MM-YYYY')}</CellPrimaryText>
        </CellText>
      )
    },
    {
      title: (
        <Text weight="medium">{t('RECONCILIATION_TABLE_TEAM_MEMBER')}</Text>
      ),
      key: 'teamMember',
      sorter: true,
      width: 220,
      render: (_, record) => {
        const name = formatDisplayName(record.teamMember.name)
        return (
          <CellWithAvatar>
            <AvatarCell>
              <Avatar
                url={record.teamMember.avatarUrl ?? undefined}
                size={22}
              />
            </AvatarCell>
            <CellPrimaryText>{name}</CellPrimaryText>
          </CellWithAvatar>
        )
      }
    },
    {
      title: <Text weight="medium">{nominative}</Text>,
      key: 'serviceUser',
      sorter: true,
      width: 220,
      render: (_, record) => {
        if (!record.serviceUser) {
          return (
            <CellText>
              <CellPrimaryText>-</CellPrimaryText>
            </CellText>
          )
        }
        const name = formatDisplayName(record.serviceUser.name)
        return (
          <CellWithAvatar>
            <AvatarCell>
              <Avatar
                url={record.serviceUser.avatarUrl ?? undefined}
                size={22}
              />
            </AvatarCell>
            <CellPrimaryText>{name}</CellPrimaryText>
          </CellWithAvatar>
        )
      }
    },
    {
      title: (
        <Text weight="medium">{t('RECONCILIATION_TABLE_TYPE_OF_EVENT')}</Text>
      ),
      key: 'eventType',
      sorter: true,
      width: 160,
      render: (_, record) => (
        <CellText>
          <CellPrimaryText>{record.eventType.name}</CellPrimaryText>
        </CellText>
      )
    },
    {
      title: (
        <MultilineHeaderText weight="medium">
          {t('RECONCILIATION_TABLE_PLANNED_TIME_DURATION')}
        </MultilineHeaderText>
      ),
      key: 'plannedTime',
      sorter: true,
      width: 140,
      onHeaderCell: () => ({
        className: 'reconciliation-header-multiline'
      }),
      render: (_, record) => (
        <TimeStack>
          <CellPrimaryText>
            {formatTime(record.plannedTime.startTime, record.timezone)}–
            {formatTime(record.plannedTime.endTime, record.timezone)}
          </CellPrimaryText>
          <CellSecondaryText>
            {formatMinutes(record.plannedTime.durationMinutes)}
          </CellSecondaryText>
        </TimeStack>
      )
    },
    {
      title: (
        <MultilineHeaderText weight="medium">
          {t('RECONCILIATION_TABLE_ACTUAL_TIME_DURATION')}
        </MultilineHeaderText>
      ),
      key: 'actualTime',
      sorter: true,
      width: 160,
      onHeaderCell: () => ({
        className: 'reconciliation-header-multiline'
      }),
      render: (_, record) => {
        if (record.isCanceled) {
          return (
            <CellText>
              <DangerText>
                <Text>{t('RECONCILIATION_EVENT_CANCELED')}</Text>
              </DangerText>
            </CellText>
          )
        }
        if (!record.actualTime) {
          return (
            <CellText>
              <DangerText>
                <Text>{t('MISSED')}</Text>
              </DangerText>
            </CellText>
          )
        }
        if (!isCompleteActualTime(record.actualTime)) {
          return (
            <TimeStack>
              <CellPrimaryText>
                {t('RECONCILIATION_ACTUAL_TIME_CLOCK_IN', {
                  time: formatTime(record.actualTime.startTime, record.timezone)
                })}
              </CellPrimaryText>
              <CellSecondaryText>
                {t('RECONCILIATION_ACTUAL_TIME_ONGOING')}
              </CellSecondaryText>
            </TimeStack>
          )
        }
        const at = record.actualTime
        return (
          <TimeStack>
            <CellPrimaryText>
              {formatTime(at.startTime, record.timezone)}–
              {formatTime(at.endTime, record.timezone)}
            </CellPrimaryText>
            <CellSecondaryText>
              {formatMinutes(at.durationMinutes)}
            </CellSecondaryText>
          </TimeStack>
        )
      }
    },
    {
      title: (
        <MultilineHeaderText weight="medium">
          {t('RECONCILIATION_TABLE_DURATION_DISCREPANCY')}
        </MultilineHeaderText>
      ),
      key: 'durationDiscrepancy',
      sorter: true,
      width: 100,
      onHeaderCell: () => ({
        className: 'reconciliation-header-multiline'
      }),
      render: (_, record) => {
        if (record.isCanceled) {
          return null
        }
        const diff = record.durationDiscrepancy
        if (diff === null) {
          return (
            <CellText>
              <CellPrimaryText>-</CellPrimaryText>
            </CellText>
          )
        }
        return (
          <CellText>
            <CellPrimaryText>
              {diff > 0 ? '-' : diff < 0 ? '+' : ''}
              {formatMinutes(Math.abs(diff))}
            </CellPrimaryText>
          </CellText>
        )
      }
    },
    {
      title: <Text weight="medium">{t('RECONCILIATION_TABLE_WARNINGS')}</Text>,
      key: 'warningCount',
      sorter: true,
      width: 80,
      render: (_, record) => {
        if (!record.warningCount) {
          return (
            <CellText>
              <CellPrimaryText>0</CellPrimaryText>
            </CellText>
          )
        }
        const w = record.rawWarnings
        const messages: string[] = w
          ? [
              ...(w.clockInEarly > 0 ? [t('CLOCK_IN_EARLY')] : []),
              ...(w.clockInLate > 0 ? [t('CLOCK_IN_LATE')] : []),
              ...(w.clockOutEarly > 0 ? [t('CLOCK_OUT_EARLY')] : []),
              ...(w.clockOutLate > 0 ? [t('CLOCK_OUT_LATE')] : []),
              ...(w.skillMismatch ? [t('SKILL_MISMATCH')] : []),
              ...(w.clockInMismatch > 0 ? [t('CLOCK_IN_MISMATCH')] : []),
              ...(w.genderMismatch ? [t('GENDER_MISMATCH')] : []),
              ...(w.clockInLocationMismatch
                ? [t('CLOCKED_IN_LOCATION_MISMATCH')]
                : []),
              ...(w.clockOutLocationMismatch
                ? [t('CLOCKED_OUT_LOCATION_MISMATCH')]
                : [])
            ]
          : []
        return (
          <WarningPopover warningsData={messages}>
            <WarningCountTrigger>
              <CellPrimaryText>{record.warningCount}</CellPrimaryText>
            </WarningCountTrigger>
          </WarningPopover>
        )
      }
    },
    {
      title: <Text weight="medium">{t('RECONCILIATION_TABLE_PAID_TIME')}</Text>,
      key: 'paidTime',
      sorter: true,
      width: 100,
      render: (_, record) => {
        if (record.dontPayReason) {
          return (
            <Popover
              placement="top"
              showArrow
              trigger="hover"
              arrow={{ pointAtCenter: true }}
              content={
                <PopoverContentContainer>
                  <PopoverTitleText level={2}>
                    {t('RECONCILIATION_DONT_PAY_REASON_LABEL_TEXT')}
                  </PopoverTitleText>
                  <PopoverBodyText>{record.dontPayReason}</PopoverBodyText>
                </PopoverContentContainer>
              }
            >
              <CellText>
                <DangerText>
                  <Text>{t('RECONCILIATION_NOT_PAID')}</Text>
                </DangerText>
              </CellText>
            </Popover>
          )
        }
        if (record.paidTime == null) {
          return (
            <CellText>
              <CellPrimaryText>-</CellPrimaryText>
            </CellText>
          )
        }
        if (record.paidTime === 0) {
          return (
            <CellText>
              <DangerText>
                <Text>{t('RECONCILIATION_NOT_PAID')}</Text>
              </DangerText>
            </CellText>
          )
        }
        return (
          <CellText>
            <CellPrimaryText>{formatMinutes(record.paidTime)}</CellPrimaryText>
          </CellText>
        )
      }
    },
    {
      title: <Text weight="medium">{t('RECONCILIATION_TABLE_PAY_RATE')}</Text>,
      key: 'payRate',
      sorter: true,
      width: 130,
      render: (_, record) => {
        if (!record.payRate) {
          return (
            <CellText>
              <CellPrimaryText>-</CellPrimaryText>
            </CellText>
          )
        }
        const unit =
          record.payRateType === 'HOURLY'
            ? t('RATE_TYPE_HOURLY')
            : t('RATE_TYPE_FIXED')
        return (
          <TimeStack>
            <CellPrimaryText>
              {record.payRate} {unit}
            </CellPrimaryText>
            {record.isCustomInstancePayRate ? (
              <CellSecondaryText>
                {t('RECONCILIATION_CUSTOM_PAY_RATE')}
              </CellSecondaryText>
            ) : (
              record.paymentCardName && (
                <CellSecondaryText>{record.paymentCardName}</CellSecondaryText>
              )
            )}
          </TimeStack>
        )
      }
    },
    {
      title: (
        <Text weight="medium">{t('RECONCILIATION_TABLE_PAY_AMOUNT')}</Text>
      ),
      key: 'payAmount',
      sorter: true,
      width: 168,
      render: (_, record) => {
        const editButton = (
          <EditIconButton
            onClick={(e) => {
              e.stopPropagation()
              onEditPayAmount(record)
            }}
          >
            <EditOutlined />
          </EditIconButton>
        )
        const isCustomAmount =
          record.payBy === ReconciliationPayBy.CUSTOM_AMOUNT
        if (record.payAmount == null) {
          return (
            <PayAmountCell>
              {editButton}
              <CellPrimaryText>-</CellPrimaryText>
            </PayAmountCell>
          )
        }
        return (
          <PayAmountCell>
            {editButton}
            <TimeStack>
              <CellPrimaryText>
                {record.payAmount} {t('RECONCILIATION_GBP_SUFFIX')}
              </CellPrimaryText>
              {isCustomAmount && (
                <CellSecondaryText>
                  {t('RECONCILIATION_CUSTOM_PAY_AMOUNT')}
                </CellSecondaryText>
              )}
            </TimeStack>
          </PayAmountCell>
        )
      }
    },
    {
      title: '',
      key: 'dontPay',
      width: 120,
      render: (_, record) => {
        if (record.dontPayReason) {
          return null
        }
        return (
          <Button
            variant="text"
            onClick={(e) => {
              e.stopPropagation()
              onDontPay(record)
            }}
          >
            {t('RECONCILIATION_PAY_TYPE_DONT_PAY')}
          </Button>
        )
      }
    }
  ]

  return columns
}

const MultilineHeaderText = styled(Text)`
  white-space: pre-line;
`

const ReconciliationTableTag = styled(Tag)`
  padding: 4px 8px;
  font-size: 12px;
`

const LeftmostCellContainer = styled.div`
  padding-left: 12px;
`

const CellText = styled.div``

const CellPrimaryText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const CellSecondaryText = styled(Text).attrs({
  level: 2,
  weight: 'medium'
})`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const CellWithAvatar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`

const AvatarCell = styled.div`
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  overflow: hidden;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  .ant-avatar {
    width: 22px !important;
    height: 22px !important;
    line-height: 22px !important;
    flex-shrink: 0;
  }

  .ant-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const TimeStack = styled.div`
  display: flex;
  flex-direction: column;
`

const DangerText = styled.span`
  color: #f5222d;
`

const WarningCountTrigger = styled.span`
  cursor: pointer;
`

const PayAmountCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const EditIconButton = styled.button`
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  color: ${({ theme }) => theme.color.neutral.color3};
  font-size: 16px;
  transition: color 0.15s ease;

  &:hover {
    color: #5b8def;
  }

  &:active {
    color: #2f6ad0;
  }
`

const PopoverContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 280px;
`

const PopoverTitleText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const PopoverBodyText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`
